import type { ChipId } from '../types'
import { CHIP_ICONS } from './chipIcons'
// Imported here as well as in GameweekDetail: the flying clone renders this block
// from a portal at document.body, without the modal ever having mounted.
import './GameweekDetail.css'

interface GameweekDetailHeadingProps {
  gameweek: number
  totalPoints: number
  activeChips: ChipId[]
  /** Another player's name, when the modal was opened from their profile. Absent
   *  on your own gameweeks — including the picker's shared-element clone, which never passes it,
   *  so that flight is unchanged. */
  ownerName?: string
  /** Their *best* gameweek reads "NAME'S SHARPEST WEEK"; any other week of theirs reads
   *  "NAME'S GW HISTORY", since a week a chip was spent in isn't a claim about it being their
   *  best. The number is in the `Gameweek N` title directly below, so the eyebrow doesn't
   *  repeat it. */
  ownerIsBestGameweek?: boolean
  /** Attached by the results modal so the shared-element flight can measure its destination.
   * The flying clone renders this same component without a ref. */
  anchorRef?: (el: HTMLElement | null) => void
}

/** The results modal's heading block — chip badges, `Gameweek N`, `N pts`. It is also
 * the destination of the gameweek picker's shared-element expand, and the flying
 * clone renders this very component, so the clone's final frame is a pixel match for the real
 * heading by construction rather than by two markups being kept in sync by hand. */
function GameweekDetailHeading({
  gameweek,
  totalPoints,
  activeChips,
  ownerName,
  ownerIsBestGameweek,
  anchorRef,
}: GameweekDetailHeadingProps) {
  return (
    <div className="gameweek-detail__nav-heading" ref={anchorRef}>
      {ownerName && (
        <p className="gameweek-detail__owner label-eyebrow">
          {/* Same wording as the profile row this opens from — one record must not have two
              names a tap apart. */}
          {ownerName}'s {ownerIsBestGameweek ? 'sharpest week' : 'GW history'}
        </p>
      )}
      {activeChips.length > 0 && (
        <div className="gameweek-detail__chip-badges">
          {activeChips.map((chipId) => {
            const Icon = CHIP_ICONS[chipId]
            return (
              <span
                key={chipId}
                className="gameweek-detail__chip-badge"
                aria-label={`${chipId} chip used`}
              >
                <Icon aria-hidden="true" />
              </span>
            )
          })}
        </div>
      )}
      <p className="gameweek-detail__title">Gameweek {gameweek}</p>
      <p className="gameweek-detail__points">{totalPoints} pts</p>
    </div>
  )
}

export default GameweekDetailHeading
